/**
 * Frontend registry for admin audit actions and target types.
 * Keep in sync with functions/admin/auditMetadata.js when Lot 4.3 adds writes.
 * Labels live in LanguageContext; this file only stores values and i18n keys.
 */

export const ADMIN_AUDIT_PAGE_SIZE = 25;

export const ADMIN_AUDIT_FILTER_TYPES = Object.freeze(['all', 'action', 'actor', 'targetType']);

export const ADMIN_AUDIT_SUMMARY_UNKNOWN = 'admin.audit.summary.unknown';

/** @type {{ value: string, summaryKey: string, filterLabelKey: string }[]} */
export const ADMIN_AUDIT_ACTIONS = Object.freeze([
  {
    value: 'role_changed',
    summaryKey: 'admin.audit.summary.roleChanged',
    filterLabelKey: 'admin.audit.filter.action.roleChanged',
  },
  {
    value: 'impersonation_started',
    summaryKey: 'admin.audit.summary.impersonationStarted',
    filterLabelKey: 'admin.audit.filter.action.impersonationStarted',
  },
  {
    value: 'impersonation_ended',
    summaryKey: 'admin.audit.summary.impersonationEnded',
    filterLabelKey: 'admin.audit.filter.action.impersonationEnded',
  },
]);

/** Observed targetType values in the repo. Do not invent types. */
export const ADMIN_AUDIT_TARGET_TYPES = Object.freeze([
  {
    value: 'user',
    labelKey: 'admin.audit.targetType.user',
  },
]);

const ACTIONS_BY_VALUE = new Map(ADMIN_AUDIT_ACTIONS.map((item) => [item.value, item]));

/**
 * @param {unknown} action
 * @returns {(typeof ADMIN_AUDIT_ACTIONS)[number] | null}
 */
export function getAdminAuditAction(action) {
  if (typeof action !== 'string') return null;
  return ACTIONS_BY_VALUE.get(action.trim()) || null;
}

/**
 * Prefer the backend summaryKey; fall back to the registry; unknown shares one key.
 * @param {{ action?: unknown, summaryKey?: unknown }} log
 * @returns {string}
 */
export function resolveAdminAuditSummaryKey(log) {
  const fromLog = typeof log?.summaryKey === 'string' ? log.summaryKey.trim() : '';
  if (fromLog) return fromLog;
  return getAdminAuditAction(log?.action)?.summaryKey || ADMIN_AUDIT_SUMMARY_UNKNOWN;
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isKnownAdminAuditTargetType(value) {
  if (typeof value !== 'string') return false;
  return ADMIN_AUDIT_TARGET_TYPES.some((item) => item.value === value.trim());
}
